/**
 * Raw mutation service fns for the approval-chain definitions domain.
 *
 * The matching read services + queryOptions helpers (listApprovalChains,
 * getApprovalChain, approvalChainsQuery, approvalChainQuery)
 * live canonically in `entities/approval-chain`. The mutations belong to this
 * feature per AD-23.
 */
import { WorkflowDefinitionsService } from '@inference.engine/workspace-api-client/web';
import type {
  CreateApprovalChainInput,
  DeleteApprovalChainResult,
  UpdateApprovalChainInput,
  ApprovalChain,
} from '@/entities/approval-chain';
import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

export const deleteApprovalChain = async (
  projectId: string,
  approvalChainId: string,
): Promise<DeleteApprovalChainResult> => {
  const response = await WorkflowDefinitionsService.delete({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
    // The list view owns error UI (toast via parseProblemDetail); suppress the
    // global interceptor toast so the user doesn't see two stacked toasts.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to delete approval chain');
  }

  return response.data;
};

export const duplicateApprovalChain = async (
  projectId: string,
  approvalChainId: string,
): Promise<ApprovalChain> => {
  const response = await WorkflowDefinitionsService.duplicate({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to duplicate approval chain');
  }

  return response.data;
};

export const createApprovalChain = async (
  projectId: string,
  body: CreateApprovalChainInput,
): Promise<ApprovalChain> => {
  const response = await WorkflowDefinitionsService.create({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    body,
    // The editor owns error UI (toast via parseProblemDetail); suppress the
    // global interceptor toast so the user doesn't see two stacked toasts.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to create approval chain');
  }

  return response.data;
};

export const updateApprovalChain = async (
  projectId: string,
  approvalChainId: string,
  body: UpdateApprovalChainInput,
): Promise<ApprovalChain> => {
  const response = await WorkflowDefinitionsService.update({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
    body,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to update approval chain');
  }

  return response.data;
};
